import { FC } from 'react';
import { TextField } from '../../../atoms/text-field/text-field';
import { Button } from '../../../atoms/button/button';
import { StyledDiv } from '../../../common/components/styled-div';
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Form,
} from '../../../molecules/form/form';
import {
  observationFieldType,
  ObservationField,
} from '../../coaching/observation/observation-setup/observation-setup';
import { DateTextField } from '../../../molecules/date-text-field/date-text-field';
import { SearchWithSuggestionsMulti } from '../../../molecules/search-with-suggestions/search-with-suggestion-multi';
import { Typography } from '../../../atoms/progress-with-label/progress-with-label';
import { Select } from '../../../atoms/select/select';
import { SignupGender } from '../../signup-form/signup-form';
import { RadioGroup, RadioOption } from '../../../atoms/radio/radio';
import {
  Control,
  FieldValues,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';
import {
  IProfessionalTrainingRecord,
  IQualificationRecord,
  ServiceDesignation,
  SchoolLevel,
} from '@taleemabad/db';

interface EditUserProfileProps {
  fields: ObservationField[];
  form: UseFormReturn<any>;
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

interface UserProfileFormData extends FieldValues {
  cnic: string;
  gender?: string;
  dateOfBirth?: string;
  joiningDate: string;
  everPromoted?: boolean;
  lastPromotionDate?: string;
  jobType?: string;
  serviceDesignation?: string;
  customServiceDesignation?: string;
  basicPayScale?: string;
  levels?: string[];
  qualifications: IQualificationRecord[];
  professionalTrainings: IProfessionalTrainingRecord[];
  hasReceivedTraining?: boolean;
}

// Helper function to transform form values to suggestion items
const transformValueToSuggestions = (value: any, suggestions: any[] = []) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((id: string) => suggestions.find((s) => s.id === id))
    .filter(
      (item): item is { id: string; label: string; name?: string } =>
        item !== undefined,
    );
};

// Helper function to handle multi-select changes
const handleMultiSelectChange = (items: any[], formField: any, form: any) => {
  const selectedIds = items.map((item) => item.id);
  formField.onChange(selectedIds);
  form.trigger(formField.name);
};

// Shared constant: today's date in YYYY-MM-DD format
const TODAY_DATE = new Date().toISOString().split('T')[0];

// Default training entry structure
const DEFAULT_TRAINING_ENTRY = {
  trainingTitle: '',
  givenBy: '',
  date: '',
} as const;

// Default qualification entry structure
const DEFAULT_QUALIFICATION_ENTRY = {
  degreeType: '',
  customDegreeType: '',
  degreeTitle: '',
  passingYear: new Date().getFullYear(),
} as const;

// Profile field names enums
export enum ProfileFieldNames {
  GENDER = 'gender',
  JOB_TYPE = 'jobType',
  SERVICE_DESIGNATION = 'serviceDesignation',
  CUSTOM_SERVICE_DESIGNATION = 'customServiceDesignation',
  BASIC_PAY_SCALE = 'basicPayScale',
  CNIC = 'cnic',
  JOINING_DATE = 'joiningDate',
  DATE_OF_BIRTH = 'dateOfBirth',
  EVER_PROMOTED = 'everPromoted',
  LAST_PROMOTION_DATE = 'lastPromotionDate',
  HAS_RECEIVED_TRAINING = 'hasReceivedTraining',
  LEVELS = 'levels',
}

// Degree type enum for better type safety
export enum DegreeType {
  MATRIC = 'Matric / SSC',
  INTERMEDIATE = 'Intermediate / HSSC',
  BACHELORS = "Bachelor's Degree (B.A/B.Sc/B.Com)",
  BED = 'Bachelor of Education (B.Ed)',
  BFA = 'Bachelor of Fine Arts (BFA)',
  MASTERS = "Master's Degree (M.A/M.Sc/M.Com)",
  MED = 'Master of Education (M.Ed)',
  MPHIL = 'Master of Philosophy (MPhil)',
  MFA = 'Master of Fine Arts (MFA)',
  DIPLOMA = 'Diploma',
  ADVANCED_DIPLOMA = 'Advanced Diploma',
  CT = 'Certificate in Teaching (CT)',
  PIT = 'Primary Teaching Certificate (PIT)',
  PGCERT = 'Postgraduate Certificate (PGCERT)',
  PGDIP = 'Postgraduate Diploma (PGDIP)',
  PHD = 'Doctor of Philosophy (PhD)',
  EDD = 'Doctor of Education (Ed.D)',
  OTHER = 'Other',
}

// Degree options for teachers
export const degreeOptions = [
  // Basic Education
  { value: DegreeType.MATRIC, content: 'Matric / SSC' },
  { value: DegreeType.INTERMEDIATE, content: 'Intermediate / HSSC' },

  // Undergraduate Degrees
  {
    value: DegreeType.BACHELORS,
    content: "Bachelor's Degree (B.A/B.Sc/B.Com)",
  },
  { value: DegreeType.BED, content: 'Bachelor of Education (B.Ed)' },
  { value: DegreeType.BFA, content: 'Bachelor of Fine Arts (BFA)' },

  // Master's Degrees
  { value: DegreeType.MASTERS, content: "Master's Degree (M.A/M.Sc/M.Com)" },
  { value: DegreeType.MED, content: 'Master of Education (M.Ed)' },
  { value: DegreeType.MPHIL, content: 'Master of Philosophy (MPhil)' },
  { value: DegreeType.MFA, content: 'Master of Fine Arts (MFA)' },

  // Professional Diplomas
  { value: DegreeType.DIPLOMA, content: 'Diploma' },
  { value: DegreeType.ADVANCED_DIPLOMA, content: 'Advanced Diploma' },
  { value: DegreeType.CT, content: 'Certificate in Teaching (CT)' },
  { value: DegreeType.PIT, content: 'Primary Teaching Certificate (PTC)' },

  // Postgraduate Qualifications
  { value: DegreeType.PGCERT, content: 'Postgraduate Certificate' },
  { value: DegreeType.PGDIP, content: 'Postgraduate Diploma' },

  // Doctorate
  { value: DegreeType.PHD, content: 'Doctor of Philosophy (PhD)' },
  { value: DegreeType.EDD, content: 'Doctor of Education (Ed.D)' },

  // Other/Custom
  { value: DegreeType.OTHER, content: 'Other (Please specify below)' },
];

const SCHOOL_LEVEL_SUGGESTIONS = [
  { id: SchoolLevel.PRIMARY, label: 'PRIMARY' },
  { id: SchoolLevel.MIDDLE, label: 'MIDDLE' },
  { id: SchoolLevel.HIGH, label: 'HIGH' },
];

// Helper function to render field component
const renderFieldComponent = (
  field: ObservationField,
  formField: any,
  fieldState: any,
  form: any,
) => {
  // Gender select dropdown
  if (field.name === ProfileFieldNames.GENDER) {
    const genderOptions = [
      { value: SignupGender.Male, content: 'Male' },
      { value: SignupGender.Female, content: 'Female' },
    ];

    return (
      <FormControl>
        <Select
          key={formField.value} // Force re-render when value changes
          options={genderOptions}
          label="Gender"
          variant="outlined"
          defaultValue={formField.value || ''}
          onChange={(option) => {
            formField.onChange(option.value);
            form.trigger(formField.name);
          }}
          width="100%"
          data-cy="profile-gender-field"
        />
      </FormControl>
    );
  }

  // Job Type dropdown
  if (field.name === ProfileFieldNames.JOB_TYPE) {
    return (
      <FormControl>
        <Select
          key={formField.value}
          options={
            field.suggestions?.map((s) => ({
              value: s.id,
              content: s.label,
            })) || []
          }
          label="Job Type"
          variant="outlined"
          defaultValue={formField.value || ''}
          onChange={(option) => {
            formField.onChange(option.value);
            form.trigger(formField.name);
          }}
          width="100%"
          data-cy="profile-job-type-field"
        />
      </FormControl>
    );
  }

  // Service Designation dropdown
  if (field.name === ProfileFieldNames.SERVICE_DESIGNATION) {
    return (
      <FormControl>
        <StyledDiv flexDirection="column" gap={16}>
          <Select
            key={formField.value}
            options={
              field.suggestions?.map((s) => ({
                value: s.id,
                content: s.label,
              })) || []
            }
            label="Service Designation"
            variant="outlined"
            defaultValue={formField.value || ''}
            onChange={(option) => {
              formField.onChange(option.value);
              form.trigger(formField.name);
              // Clear custom field when not selecting "Others"
              if (option.value !== ServiceDesignation.OTHERS) {
                form.setValue(ProfileFieldNames.CUSTOM_SERVICE_DESIGNATION, '');
              }
            }}
            width="100%"
            data-cy="profile-service-designation-field"
          />

          {formField.value === ServiceDesignation.OTHERS && (
            <FormField
              control={form.control}
              name={ProfileFieldNames.CUSTOM_SERVICE_DESIGNATION}
              render={({ field: customField }) => (
                <TextField
                  type="text"
                  label="Custom Service Designation"
                  variant="outlined"
                  value={customField.value || ''}
                  placeholder="Please specify your service designation"
                  onInput={(e) => {
                    customField.onChange(e.target.value);
                    form.trigger(customField.name);
                  }}
                  data-cy="profile-custom-designation-field"
                />
              )}
            />
          )}
        </StyledDiv>
      </FormControl>
    );
  }

  // Basic Pay Scale dropdown
  if (field.name === ProfileFieldNames.BASIC_PAY_SCALE) {
    return (
      <FormControl>
        <Select
          key={formField.value}
          options={
            field.suggestions?.map((s) => ({
              value: s.id,
              content: s.label,
            })) || []
          }
          label="Basic Pay Scale"
          variant="outlined"
          defaultValue={formField.value || ''}
          onChange={(option) => {
            formField.onChange(option.value);
            form.trigger(formField.name);
          }}
          width="100%"
          data-cy="profile-basic-pay-scale-field"
        />
      </FormControl>
    );
  }

  if (field.type === observationFieldType.dropdown && field.isMulti) {
    return (
      <SearchWithSuggestionsMulti
        value={transformValueToSuggestions(formField.value, field.suggestions)}
        suggestions={field.suggestions || []}
        placeholder={field.placeholder}
        onChange={(items) => handleMultiSelectChange(items, formField, form)}
        disabled={field.disabled}
        showDropdownOnFocus
      />
    );
  }

  // CNIC text field
  if (
    field.type === observationFieldType.text &&
    field.name === ProfileFieldNames.CNIC
  ) {
    return (
      <TextField
        type="text"
        name={formField.name}
        key={formField.name}
        label={field.placeholder}
        variant="outlined"
        value={formField.value || ''}
        maxLength={13}
        onInput={(e) => {
          formField.onChange(e.target.value);
          form.trigger(formField.name);
        }}
        errorText={''}
        data-cy="profile-cnic-field"
      />
    );
  }

  // Joining date field
  if (
    field.type === observationFieldType.text &&
    field.name === ProfileFieldNames.JOINING_DATE
  ) {
    return (
      <DateTextField
        label={field.placeholder}
        placeholder={field.placeholder}
        value={formField.value ?? ''}
        onChange={(value) => {
          formField.onChange(value);
          form.trigger(formField.name);
        }}
        error={!!fieldState.error}
        max={TODAY_DATE}
        data-cy="profile-joining-date-field"
      />
    );
  }

  // Date of birth field
  if (
    field.type === observationFieldType.text &&
    field.name === ProfileFieldNames.DATE_OF_BIRTH
  ) {
    return (
      <DateTextField
        label={field.placeholder}
        placeholder={field.placeholder}
        value={formField.value ?? ''}
        onChange={(value) => {
          formField.onChange(value);
          form.trigger(formField.name);
        }}
        error={!!fieldState.error}
        max={TODAY_DATE}
        data-cy="profile-dob-field"
      />
    );
  }

  // Last promotion date field
  if (
    field.type === observationFieldType.text &&
    field.name === ProfileFieldNames.LAST_PROMOTION_DATE
  ) {
    const everPromotedValue =
      form.watch(ProfileFieldNames.EVER_PROMOTED) ?? false;
    return (
      <StyledDiv flexDirection="column" gap={8}>
        <>
          <Typography variant="textBodyMedium">
            Have you ever been promoted?
          </Typography>
          <RadioGroup
            name="has-ever-promotion"
            direction="row"
            data-cy="ever-promoted"
            options={
              [
                {
                  value: 'true',
                  content: 'Yes',
                  checked: everPromotedValue === true,
                },
                {
                  value: 'false',
                  content: 'No',
                  checked: everPromotedValue === false,
                },
              ] as RadioOption[]
            }
            onChange={(option: RadioOption) => {
              const boolValue = option.value === 'true';
              form.setValue('everPromoted', boolValue);
              if (!boolValue) {
                formField.onChange('');
                form.trigger(formField.name);
              }
            }}
          />
        </>

        {everPromotedValue === true && (
          <>
            <Typography variant="textBodyMedium">
              Last Promotion Date
            </Typography>
            <DateTextField
              label="Last Promotion Date"
              placeholder="Last Promotion Date"
              value={formField.value ?? ''}
              onChange={(value) => {
                formField.onChange(value);
                form.trigger(formField.name);
              }}
              error={!!fieldState.error}
              max={TODAY_DATE}
              data-cy="profile-last-promotion-date-field"
            />
          </>
        )}
      </StyledDiv>
    );
  }

  // Number field
  if (field.type === observationFieldType.number) {
    return (
      <TextField
        type="number"
        name={formField.name}
        key={formField.name}
        label={field.placeholder}
        variant="outlined"
        value={Number.parseInt(formField.value) ?? ''}
        min="1"
        max="50"
        step={1}
        onInput={(e) => {
          const value = e.target.value;
          formField.onChange(value === '' ? 0 : Number.parseInt(value, 10));
          form.trigger(formField.name);
        }}
        errorText={''}
      />
    );
  }

  // Default text field
  return (
    <TextField
      type="text"
      name={formField.name}
      key={formField.name}
      label={field.placeholder}
      variant="outlined"
      value={formField.value ?? ''}
      onInput={(e) => {
        const value = e.target.value;
        formField.onChange(value);
        form.trigger(formField.name);
      }}
      errorText={''}
    />
  );
};

const renderCustomDegreeInput = (
  control: Control<UserProfileFormData>,
  index: number,
  form: UseFormReturn<UserProfileFormData>,
): JSX.Element => (
  <FormField
    control={control}
    name={`qualifications.${index}.customDegreeType`}
    render={({ field: customField }) => (
      <TextField
        type="text"
        label="Custom Degree Type"
        variant="outlined"
        value={customField.value || ''}
        placeholder="e.g., Associate Degree, Certificate, etc."
        onInput={(e) => {
          customField.onChange((e.target as HTMLInputElement).value);
          form.trigger(customField.name);
        }}
        errorText={''}
        data-cy="qualification-custom-degree-field"
      />
    )}
  />
);

export const EditUserProfile: FC<EditUserProfileProps> = ({
  fields,
  form,
  onCancel,
  onSubmit,
}) => {
  const { control } = form;

  // Field arrays for dynamic sections
  const qualificationsArray = useFieldArray({
    control,
    name: 'qualifications',
  });

  const trainingsArray = useFieldArray({
    control,
    name: 'professionalTrainings',
  });

  const handleSubmit = (data: any) => {
    onSubmit(data);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <StyledDiv
      gap={16}
      flexDirection="column"
      data-cy="edit-profile-form-container"
    >
      <StyledDiv flexDirection="column" gap={24}>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
            data-cy="edit-profile-form"
          >
            <StyledDiv flexDirection="column" gap={16} marginBottom={16}>
              {fields.map((field) => (
                <FormField
                  key={field.name}
                  control={control}
                  name={field.name}
                  render={({ field: formField, fieldState }) => (
                    <FormItem key={formField.name}>
                      <FormControl>
                        {renderFieldComponent(
                          field,
                          formField,
                          fieldState,
                          form,
                        )}
                      </FormControl>
                      {fieldState.error && <FormMessage />}
                    </FormItem>
                  )}
                />
              ))}
            </StyledDiv>

            {/* Qualifications Section */}
            <StyledDiv
              flexDirection="column"
              gap={16}
              marginBottom={24}
              data-cy="profile-qualifications-section"
            >
              <StyledDiv
                flexDirection="row"
                justifyContent="space-between"
                alignItem="center"
              >
                <Typography variant="textTitleLarge">
                  Educational Qualifications
                  <Typography variant="textTitleSmall">
                    Matric Onwards
                  </Typography>
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() =>
                    qualificationsArray.append({
                      ...DEFAULT_QUALIFICATION_ENTRY,
                      passingYear: new Date().getFullYear(), // Always use current year
                    })
                  }
                  data-cy="add-qualification-button"
                >
                  Add Qualification
                </Button>
              </StyledDiv>

              {qualificationsArray.fields.map((field, index) => (
                <StyledDiv
                  key={field.id}
                  flexDirection="column"
                  gap={12}
                  padding="var(--rk-spacing-xl)"
                  borderRadius="--rk-spacing-lg"
                  background="--md-sys-color-surface-container-low"
                  border="1px solid var(--md-sys-color-outline-variant)"
                  data-cy="qualification-card"
                >
                  <StyledDiv
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItem="center"
                  >
                    <Typography variant="textTitleMedium">
                      Qualification {index + 1}
                    </Typography>
                    <StyledDiv
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        qualificationsArray.remove(index);
                      }}
                      cursor="pointer"
                    >
                      <Button
                        type="button"
                        variant="text"
                        onClick={() => {}}
                        data-cy="remove-qualification-button"
                      >
                        Remove
                      </Button>
                    </StyledDiv>
                  </StyledDiv>

                  <FormField
                    control={control}
                    name={`qualifications.${index}.degreeType`}
                    render={({ field: formField, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <StyledDiv flexDirection="column" gap={8}>
                            <Select
                              key={`degree-type-${index}-${formField.value}`}
                              options={degreeOptions}
                              label="Degree Type"
                              variant="outlined"
                              defaultValue={formField.value || null}
                              onChange={(option) => {
                                if (option.value === DegreeType.OTHER) {
                                  formField.onChange(DegreeType.OTHER);
                                } else {
                                  formField.onChange(option.value);
                                  form.setValue(
                                    `qualifications.${index}.customDegreeType`,
                                    '',
                                  );
                                }
                                form.trigger(formField.name);
                              }}
                              width="100%"
                              data-cy="qualification-degree-type-field"
                            />

                            {formField.value === DegreeType.OTHER &&
                              renderCustomDegreeInput(control, index, form)}
                          </StyledDiv>
                        </FormControl>
                        {fieldState.error && <FormMessage />}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name={`qualifications.${index}.degreeTitle`}
                    render={({ field: formField, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <TextField
                            type="text"
                            label="Degree Title"
                            variant="outlined"
                            value={formField.value || ''}
                            placeholder="e.g., Computer Science"
                            onInput={(e) => {
                              formField.onChange(e.target.value);
                              form.trigger(formField.name);
                            }}
                            errorText={''}
                            data-cy="qualification-degree-title-field"
                          />
                        </FormControl>
                        {fieldState.error && <FormMessage />}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name={`qualifications.${index}.passingYear`}
                    render={({ field: formField, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <TextField
                            type="number"
                            label="Passing Year"
                            variant="outlined"
                            value={formField.value || new Date().getFullYear()}
                            min="1950"
                            max={new Date().getFullYear().toString()}
                            onInput={(e) => {
                              const value = parseInt(e.target.value, 10);
                              formField.onChange(value);
                              form.trigger(formField.name);
                            }}
                            errorText={''}
                            data-cy="qualification-passing-year-field"
                          />
                        </FormControl>
                        {fieldState.error && <FormMessage />}
                      </FormItem>
                    )}
                  />
                </StyledDiv>
              ))}

              {qualificationsArray.fields.length === 0 && (
                <StyledDiv
                  padding="var(--rk-spacing-xl)"
                  borderRadius="--rk-spacing-lg"
                  background="--md-sys-color-surface-variant"
                  border="1px dashed var(--md-sys-color-outline)"
                  justifyContent="center"
                >
                  <Typography variant="textBodyMedium">
                    No qualifications added yet. Click "Add Qualification" to
                    get started.
                  </Typography>
                </StyledDiv>
              )}
            </StyledDiv>

            {/* Professional Trainings Section */}
            <StyledDiv
              flexDirection="column"
              gap={16}
              marginBottom={24}
              data-cy="profile-trainings-section"
            >
              <StyledDiv flexDirection="column" gap={16}>
                <Typography variant="textTitleLarge">
                  Professional Trainings
                </Typography>
                <StyledDiv flexDirection="column" gap={8}>
                  <Typography variant="textBodyMedium">
                    Have you ever received any face to face training?
                  </Typography>
                  <RadioGroup
                    name="has-professional-trainings"
                    direction="row"
                    data-cy="has-training"
                    options={
                      [
                        {
                          value: 'true',
                          content: 'Yes',
                          checked: form.watch('hasReceivedTraining') === true,
                        },
                        {
                          value: 'false',
                          content: 'No',
                          checked: form.watch('hasReceivedTraining') === false,
                        },
                      ] as RadioOption[]
                    }
                    onChange={(option: RadioOption) => {
                      const hasTraining = option.value === 'true';
                      form.setValue('hasReceivedTraining', hasTraining);

                      if (hasTraining) {
                        // Ensure at least one training entry exists when user selects "Yes"
                        if (trainingsArray.fields.length === 0) {
                          trainingsArray.append(DEFAULT_TRAINING_ENTRY);
                        }
                      } else {
                        // Clear all training entries when user selects "No"
                        if (trainingsArray.fields.length > 0) {
                          // Remove all fields efficiently by removing from the end
                          trainingsArray.remove(
                            Array.from(
                              { length: trainingsArray.fields.length },
                              (_, i) => i,
                            ),
                          );
                        }
                        // Note: "No_Trainings" status will be handled during form submission
                      }
                    }}
                  />
                </StyledDiv>
              </StyledDiv>

              {form.watch('hasReceivedTraining') === true && (
                <>
                  <StyledDiv
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItem="center"
                    marginTop={16}
                  >
                    <Typography variant="textTitleMedium">
                      Training Details
                      <Typography variant="textTitleSmall">
                        Recent five face-to-face trainings.
                      </Typography>
                    </Typography>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        trainingsArray.append(DEFAULT_TRAINING_ENTRY)
                      }
                      data-cy="add-training-button"
                    >
                      Add Training
                    </Button>
                  </StyledDiv>

                  {trainingsArray.fields.map((field, index) => (
                    <StyledDiv
                      key={field.id}
                      flexDirection="column"
                      gap={12}
                      padding="var(--rk-spacing-xl)"
                      borderRadius="--rk-spacing-lg"
                      background="--md-sys-color-surface-container-low"
                      border="1px solid var(--md-sys-color-outline-variant)"
                      data-cy="training-card"
                    >
                      <StyledDiv
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItem="center"
                      >
                        <Typography variant="textTitleMedium">
                          Training {index + 1}
                        </Typography>
                        <StyledDiv
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            trainingsArray.remove(index);
                          }}
                          cursor="pointer"
                        >
                          <Button
                            type="button"
                            variant="text"
                            onClick={() => {}}
                            data-cy="remove-training-button"
                          >
                            Remove
                          </Button>
                        </StyledDiv>
                      </StyledDiv>

                      <FormField
                        control={control}
                        name={`professionalTrainings.${index}.trainingTitle`}
                        render={({ field: formField, fieldState }) => (
                          <FormItem>
                            <FormControl>
                              <TextField
                                type="text"
                                label="Training Title"
                                variant="outlined"
                                value={formField.value || ''}
                                onInput={(e) => {
                                  formField.onChange(e.target.value);
                                  form.trigger(formField.name);
                                }}
                                errorText={''}
                                data-cy="training-title-field"
                              />
                            </FormControl>
                            {fieldState.error && <FormMessage />}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name={`professionalTrainings.${index}.givenBy`}
                        render={({ field: formField, fieldState }) => (
                          <FormItem>
                            <FormControl>
                              <TextField
                                type="text"
                                label="Given By (Provider)"
                                variant="outlined"
                                value={formField.value || ''}
                                onInput={(e) => {
                                  formField.onChange(e.target.value);
                                  form.trigger(formField.name);
                                }}
                                errorText={''}
                                data-cy="training-provider-field"
                              />
                            </FormControl>
                            {fieldState.error && <FormMessage />}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name={`professionalTrainings.${index}.date`}
                        render={({ field: formField, fieldState }) => (
                          <FormItem>
                            <FormControl>
                              <DateTextField
                                label="Training Date"
                                placeholder="YYYY-MM-DD"
                                value={formField.value || ''}
                                onChange={(value) => {
                                  formField.onChange(value);
                                  form.trigger(formField.name);
                                }}
                                error={!!fieldState.error}
                                max={TODAY_DATE}
                                data-cy="training-date-field"
                              />
                            </FormControl>
                            {fieldState.error && <FormMessage />}
                          </FormItem>
                        )}
                      />
                    </StyledDiv>
                  ))}

                  {trainingsArray.fields.length === 0 && (
                    <StyledDiv
                      padding="var(--rk-spacing-xl)"
                      borderRadius="--rk-spacing-lg"
                      background="--md-sys-color-surface-variant"
                      border="1px dashed var(--md-sys-color-outline)"
                      justifyContent="center"
                    >
                      <Typography variant="textBodyMedium">
                        No trainings added yet. Click "Add Training" to get
                        started.
                      </Typography>
                    </StyledDiv>
                  )}
                </>
              )}
            </StyledDiv>

            {/* School Type Section - Only for PRINCIPAL and TEACHER */}
            <StyledDiv
              flexDirection="column"
              gap={16}
              marginBottom={24}
              data-cy="profile-school-type-section"
            >
              <FormField
                control={control}
                name="levels"
                render={({ field: formField, fieldState }) => {
                  // Only show this section if there are levels defined in form data
                  if (formField.value === undefined) return <></>;

                  return (
                    <FormItem>
                      <StyledDiv flexDirection="column" gap={16}>
                        <Typography variant="textTitleLarge">
                          School Type
                        </Typography>
                        <FormControl>
                          <SearchWithSuggestionsMulti
                            value={transformValueToSuggestions(
                              formField.value,
                              SCHOOL_LEVEL_SUGGESTIONS,
                            )}
                            suggestions={SCHOOL_LEVEL_SUGGESTIONS}
                            placeholder="Select your School Type"
                            onChange={(items) =>
                              handleMultiSelectChange(items, formField, form)
                            }
                            disabled={false}
                            showDropdownOnFocus
                            data-cy="profile-levels-field"
                          />
                        </FormControl>
                        {fieldState.error && <FormMessage />}
                      </StyledDiv>
                    </FormItem>
                  );
                }}
              />
            </StyledDiv>

            <StyledDiv flexDirection="column" gap={8}>
              <Button type="submit" data-cy="save-profile-button">
                Save Changes
              </Button>
            </StyledDiv>
          </form>
        </Form>
      </StyledDiv>
    </StyledDiv>
  );
};
